/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Popover } from 'antd'
import dayjs from 'dayjs'
import colors from '@sportninja/common/constants/appColors'
import { notificationTypes } from '@sportninja/common/constants/notificationsTypes'
import Icon from '../../Icon'
import Text from '../../ts-components/Text'
import { Invite } from '../css'
import {
  InvitationsIcon,
  SocialFeedIcon,
  GameStatusIcon,
  MoreVerticalIcon,
} from './icons'
import { NotificationItem as NotificationItemType, ParentType } from './types'
import { DeleteNotificationModal } from './ConfirmationModals'

/**
 * Props for the NotificationItem component
 */
export interface NotificationItemProps {
  /** Notification data object */
  item: NotificationItemType
  /** Callback when notification is clicked */
  onClick: () => void
  /** Callback to mark notification as read */
  setNotificationAsRead: (id: string) => Promise<void>
  /** Callback to mark notification as unread */
  setNotificationAsUnread: (id: string) => Promise<void>
  /** Callback when delete button is pressed */
  onPressDelete: () => Promise<void>
  /** Callback to set which notification has options menu open */
  setNotificationOpenForDelete: (id: string | null) => void
  /** ID of notification that currently has options menu open */
  notificationOpenForDelete: string | null
}

/**
 * List of all valid notification types
 */
const notificationTypesList = Object.values(notificationTypes)

/**
 * Formats notification date based on when it was created
 * @param {string} date - ISO date string
 * @returns {string} Formatted date - time if today (12:19 AM), date otherwise (May 6, 2025)
 */
const getNotificationDate = (date: string): string => {
  if (typeof date !== 'string') {
    return date
  }

  const notificationDate = dayjs(date)
  const today = dayjs().startOf('day')
  const isToday = notificationDate.isSame(today, 'day')

  if (isToday) {
    // Format as time: 12:19 AM
    return notificationDate.format('h:mm A')
  } else {
    // Format as date: May 6, 2025
    return notificationDate.format('MMM D, YYYY')
  }
}

/**
 * Maps notification types to their corresponding icon components
 * @param {string} notificationType - The type of notification
 * @returns {React.FC} The icon component for the notification type
 */
const getNotificationIcon = (notificationType: string): React.FC => {
  switch (notificationType) {
    case notificationTypes.INVITATION:
      return InvitationsIcon
    case notificationTypes.SOCIAL_FEED:
      return SocialFeedIcon
    case notificationTypes.GAME_FINISHED:
    case notificationTypes.GAME_STATUS_UPDATE:
    case notificationTypes.GAME_RESCHEDULED_UPDATE:
    case notificationTypes.UPCOMING_GAME:
    case notificationTypes.GAME_SCORED_NOTIFICATION:
      return GameStatusIcon
    default:
      return InvitationsIcon // Default fallback
  }
}

/**
 * Extracts the base URL for a given parent type and ID
 * @param {ParentType} parentType - The type of the parent entity
 * @param {string} parentId - The ID of the parent entity
 * @returns {string} The constructed base URL for the entity
 */
const getBaseUrl = (
  parentType: ParentType | undefined,
  parentId: string | undefined
): string => {
  if (!parentType || !parentId) {
    return ''
  }

  const urlMap: Record<ParentType, string> = {
    organization: `/org/${parentId}`,
    team: `/team/${parentId}`,
    schedule: `/schedule/${parentId}`,
  }
  return urlMap[parentType] || ''
}

/**
 * Constructs feed URL from notification item
 * @param {NotificationItemType} item - Notification item
 * @returns {string} Feed URL
 */
const getFeedUrl = (item: NotificationItemType): string => {
  try {
    if (!item?.data?.action_url) {
      return getBaseUrl(item?.data?.parent_type, item?.data?.parent_id)
    }
    const actionUrl = item?.data?.action_url || ''
    const parentType = item?.data?.parent_type
    const parentId = item?.data?.parent_id
    const baseUrl = getBaseUrl(parentType, parentId)
    const feedId = actionUrl.split('/').pop()
    return `${baseUrl}/feed/${feedId}`
  } catch (e) {
    console.error('🚨 getFeedUrl: Error constructing feed URL', e)
    return getBaseUrl(item?.data?.parent_type, item?.data?.parent_id) || ''
  }
}

/**
 * Gets navigation URL for a notification based on its type
 * @param {NotificationItemType} item - Notification item
 * @returns {string} Navigation URL
 */
const getUrl = (item: NotificationItemType): string => {
  const returnUrl = getFeedUrl(item)
  switch (item.type) {
    case notificationTypes.GAME_FINISHED:
    case notificationTypes.LIVE_STREAM:
    case notificationTypes.UPCOMING_GAME:
    case notificationTypes.ATTENDANCE:
    case notificationTypes.GAME_SCORED_NOTIFICATION:
    case notificationTypes.GAME_STATUS_UPDATE:
    case notificationTypes.GAME_RESCHEDULED_UPDATE:
    case notificationTypes.OFFENSE:
    case notificationTypes.PERIOD:
      return `/game/${item?.data?.entity_id}`
    case notificationTypes.PLAYER_SUSPENDED:
    case notificationTypes.PLAYER_SUSPENDED_COMPLETED:
      return '/profile/suspensions'
    case notificationTypes.INVITATION:
      return `/invitations/${item?.data?.invitation_id}`
    case notificationTypes.SOCIAL_FEED:
      return returnUrl
    default:
      return ''
  }
}

/**
 * Renders rich text content for notifications with non-clickable links
 * @param {string} content - The HTML content to render
 * @param {number} maxLength - Maximum length before truncating
 * @returns {string} Sanitized HTML with non-clickable links
 */
const renderNotificationContent = (
  content: string,
  maxLength: number = 200
): string => {
  if (!content?.trim()) {
    return ''
  }

  // Truncate content if needed
  const truncatedContent =
    content.length > maxLength
      ? content.substring(0, maxLength) + '...'
      : content

  // Parse HTML and remove clickable functionality from links
  const parser = new DOMParser()
  const doc = parser.parseFromString(truncatedContent, 'text/html')

  // Find all anchor tags and convert them to spans with link styling
  const anchors = doc.querySelectorAll('a')
  anchors.forEach((anchor) => {
    const href = anchor.getAttribute('href')
    const textContent = anchor.textContent

    // Create a span element to replace the anchor
    const span = doc.createElement('span')
    span.textContent = textContent
    span.style.cssText =
      'color: #F2B400; text-decoration: underline; cursor: default;'
    span.title = href || ''

    // Replace the anchor with the span
    if (anchor.parentNode) {
      anchor.parentNode.replaceChild(span, anchor)
    }
  })

  return doc.body.innerHTML
}

/**
 * Pending status icon for invitation notifications
 */
const PendingStatusIcon: React.FC = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='16'
    height='16'
    viewBox='0 0 16 16'
    fill='none'
  >
    <path
      d='M10.0667 12.9133L10.7334 14.0733C10.0934 14.3667 9.39337 14.56 8.6667 14.6333V13.2867C9.16003 13.2267 9.6267 13.1 10.0667 12.9133ZM2.71337 8.66667H1.3667C1.44003 9.4 1.63337 10.0933 1.9267 10.7333L3.0867 10.0667C2.90003 9.62667 2.77337 9.16 2.71337 8.66667ZM10.0667 3.08667L10.7334 1.92667C10.0934 1.63334 9.40003 1.44 8.6667 1.36667V2.71334C9.16003 2.77334 9.6267 2.9 10.0667 3.08667ZM13.2867 7.33334H14.6334C14.56 6.6 14.3667 5.90667 14.0734 5.26667L12.9134 5.93334C13.1 6.37334 13.2267 6.84 13.2867 7.33334ZM5.93337 12.9133L5.2667 14.0733C5.9067 14.3667 6.6067 14.56 7.33337 14.6333V13.2867C6.84003 13.2267 6.37337 13.1 5.93337 12.9133ZM7.33337 2.71334V1.36667C6.60003 1.44 5.9067 1.63334 5.2667 1.92667L5.93337 3.08667C6.37337 2.9 6.84003 2.77334 7.33337 2.71334ZM12.24 4.78L13.4 4.10667C12.98 3.52667 12.4667 3.01334 11.8867 2.59334L11.2134 3.75334C11.6067 4.05334 11.9467 4.39334 12.24 4.78ZM3.0867 5.93334L1.9267 5.26667C1.63337 5.90667 1.44003 6.6 1.3667 7.33334H2.71337C2.77337 6.84 2.90003 6.37334 3.0867 5.93334ZM13.2867 8.66667C13.2267 9.16 13.1 9.62667 12.9134 10.0667L14.0734 10.7333C14.3667 10.0933 14.56 9.39334 14.6334 8.66667H13.2867ZM11.22 12.24L11.8934 13.4C12.4734 12.98 12.9867 12.4667 13.4067 11.8867L12.2467 11.2133C11.9467 11.6067 11.6067 11.9467 11.22 12.24ZM4.78003 3.76L4.11337 2.59334C3.5267 3.02 3.02003 3.52667 2.60003 4.11334L3.76003 4.78667C4.05337 4.39334 4.39337 4.05334 4.78003 3.76ZM3.76003 11.22L2.60003 11.8867C3.02003 12.4667 3.53337 12.98 4.11337 13.4L4.7867 12.24C4.39337 11.9467 4.05337 11.6067 3.76003 11.22ZM8.00003 4.66667C7.63337 4.66667 7.33337 4.96667 7.33337 5.33334V7.72667C7.33337 8.08 7.47337 8.42 7.7267 8.66667L9.7267 10.6667C9.9867 10.9267 10.4067 10.9267 10.6667 10.6667C10.9267 10.4067 10.9267 9.98667 10.6667 9.72667L8.6667 7.72667V5.33334C8.6667 4.96667 8.3667 4.66667 8.00003 4.66667Z'
      fill='#FFC107'
    />
  </svg>
)

/**
 * Accepted status icon for invitation notifications
 */
const AcceptedStatusIcon: React.FC = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='16'
    height='16'
    viewBox='0 0 16 16'
    fill='none'
  >
    <path
      d='M5.86337 10.5833L3.55004 8.27002C3.29004 8.01002 2.87004 8.01002 2.61004 8.27002C2.35004 8.53002 2.35004 8.95002 2.61004 9.21002L5.39671 11.9967C5.65671 12.2567 6.07671 12.2567 6.33671 11.9967L13.39 4.94335C13.65 4.68335 13.65 4.26335 13.39 4.00335C13.13 3.74335 12.71 3.74335 12.45 4.00335L5.86337 10.5833Z'
      fill='#10AD32'
    />
  </svg>
)

/**
 * Props for InvitationStatusBadge component
 */
interface StatusBadgeProps {
  /** Accepted timestamp - null means pending, non-null means accepted */
  accepted_at: string | null
}

/**
 * Renders status badge for invitation notifications
 * Shows "Pending" if accepted_at is null, "Accepted" otherwise
 *
 * @param {StatusBadgeProps} props - Component props
 * @returns {JSX.Element} Status badge with icon and text
 */
const InvitationStatusBadge: React.FC<StatusBadgeProps> = ({ accepted_at }) => {
  console.log('🔄 InvitationStatusBadge: Rendering status badge', {
    accepted_at,
    status: accepted_at === null ? 'pending' : 'accepted',
  })

  const isPending = accepted_at === null
  const StatusIcon = isPending ? PendingStatusIcon : AcceptedStatusIcon
  const statusText = isPending ? 'Pending' : 'Accepted'

  // Based on Figma designs:
  // - Pending: Warning/yellow theme (#FFC107)
  // - Accepted: Success/green theme (#10AD32)
  // - No background, just icon + text
  const textColor = isPending ? colors.WARNING_300 : colors.SUCCESS_300

  return (
    <div
      css={css`
        display: inline-flex;
        align-items: center;
        gap: 4px;
        flex-shrink: 0;
      `}
      aria-label={`Invitation status: ${statusText}`}
    >
      <StatusIcon />
      <Text variant='paragraphXSmall' weight='regular' color={textColor}>
        {statusText}
      </Text>
    </div>
  )
}

/**
 * NotificationItem component - renders a single notification in the list
 *
 * Features:
 * - Type-specific icons and styling
 * - Rich text content for social feed notifications
 * - Unread indicator with distinct background
 * - Click to navigate to notification target
 * - Options menu for deletion
 * - Automatic read marking on click
 *
 * @param {NotificationItemProps} props - Component props
 * @returns {JSX.Element} Notification item with content and actions
 */
const NotificationItem: React.FC<NotificationItemProps> = ({
  item,
  onClick,
  setNotificationAsRead,
  setNotificationAsUnread,
  onPressDelete,
  setNotificationOpenForDelete,
  notificationOpenForDelete,
}) => {
  const history = useHistory()
  const isRecognizedType = notificationTypesList.includes(item?.type)
  const isSocialFeed = item?.type === notificationTypes.SOCIAL_FEED
  const isInvitation = item?.type === notificationTypes.INVITATION
  const hasInvitationData = item?.invitation !== undefined
  const isUnread = !item?.read_at
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const isOptionsOpen = notificationOpenForDelete === item?.id

  const handleNotificationClick = async () => {
    console.log('🔄 NotificationItem: Notification clicked', { id: item.id })

    // Don't navigate if popover is open
    if (isOptionsOpen) {
      console.log('🔄 NotificationItem: Ignoring click - popover is open', {
        id: item.id,
      })
      return
    }

    if (isRecognizedType) {
      await setNotificationAsRead(item.id)
      const url = getUrl(item)
      if (url) {
        history.push(url)
      }
      onClick()
    }
  }

  /**
   * Handles marking notification as unread
   */
  const handleMarkAsUnread = async () => {
    console.log('🔄 NotificationItem: Mark as unread clicked', {
      id: item.id,
    })
    setNotificationOpenForDelete(null) // Close popover
    await setNotificationAsUnread(item.id)
  }

  /**
   * Handles opening the delete confirmation modal
   */
  const handleDeleteClick = () => {
    console.log('🔄 NotificationItem: Delete button clicked', { id: item.id })
    setDeleteError(null)
    setIsDeleteModalOpen(true)
    setNotificationOpenForDelete(null) // Close popover
  }

  /**
   * Handles delete confirmation from modal
   */
  const handleDeleteConfirm = async () => {
    console.log('🔄 NotificationItem: Delete confirmed', { id: item.id })
    try {
      setIsDeleting(true)
      setDeleteError(null)
      await onPressDelete()
      setIsDeleteModalOpen(false)
    } catch (error: any) {
      console.error('🚨 NotificationItem: Error deleting notification', {
        error,
      })
      setDeleteError(error?.message || 'Failed to delete notification')
    } finally {
      setIsDeleting(false)
    }
  }

  /**
   * Handles closing the delete modal
   */
  const handleDeleteModalClose = () => {
    setIsDeleteModalOpen(false)
    setDeleteError(null)
  }

  const NotificationIcon = getNotificationIcon(item?.type)

  /**
   * Popover content for notification options menu
   */
  const popoverContent = (
    <div
      onClick={(e) => e.stopPropagation()}
      css={css`
        display: flex;
        flex-direction: column;
        min-width: 150px;
      `}
    >
      {item?.read_at && (
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 12px;
            cursor: pointer;
            &:hover {
              background-color: rgba(255, 255, 255, 0.1);
            }
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          `}
          onClick={(e) => {
            e.stopPropagation()
            handleMarkAsUnread()
          }}
        >
          <Icon name='envelope-open' fontSize={16} />
          <span
            css={css`
              font-size: 14px;
              color: white;
            `}
          >
            Mark as Unread
          </span>
        </div>
      )}
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px;
          cursor: pointer;
          &:hover {
            background-color: rgba(255, 255, 255, 0.1);
          }
        `}
        onClick={(e) => {
          e.stopPropagation()
          handleDeleteClick()
        }}
      >
        <Icon name='trash' fontSize={16} />
        <span
          css={css`
            font-size: 14px;
            color: white;
          `}
        >
          Delete
        </span>
      </div>
    </div>
  )

  return (
    <Invite
      css={css`
        position: relative;
        align-items: flex-start !important;
        gap: 8px;
        margin-bottom: 0;
        cursor: pointer;
        ${isUnread &&
        css`
          border-radius: 8px;
          background: ${'#313B4B'};
          padding: 8px;
        `}
      `}
      onClick={() => handleNotificationClick()}
    >
      {/* Icon with conditional styling based on read status */}
      <div
        css={css`
          display: flex;
          flex-shrink: 0;
        `}
      >
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: center;
            width: 32px;
            height: 32px;
            border-radius: 50%;
            background: ${isUnread ? colors.INFO_300 : colors.SECONDARY_100};
            padding: 8px;
          `}
        >
          <NotificationIcon />
        </div>
      </div>
      <div
        className='nav-link'
        css={css`
          flex: 1;
        `}
      >
        <div className='title'>
          <Text
            variant='paragraphXSmall'
            weight='bold'
            color={colors.NEUTRAL_50}
          >
            {item?.data?.title}
          </Text>
        </div>
        <div
          className='flex type'
          css={css`
            align-items: center;
            color: ${colors.NEUTRAL_0};
            opacity: 1;
          `}
        >
          {isSocialFeed ? (
            <div
              css={css`
                color: ${colors.NEUTRAL_0};
                font-family: inherit;
                line-height: 1.5;
                font-size: 14px;
                font-weight: 500;

                /* Style the rich text content */
                h1,
                h2,
                h3 {
                  font-weight: 600;
                  color: ${colors.NEUTRAL_0};
                  margin: 8px 0 6px 0;
                  font-size: 14px;
                }

                p {
                  color: ${colors.NEUTRAL_0};
                  margin: 6px 0;
                }

                strong {
                  font-weight: 600;
                  color: ${colors.NEUTRAL_0};
                }

                em {
                  font-style: italic;
                  color: ${colors.NEUTRAL_0};
                }

                u {
                  text-decoration: underline;
                  color: ${colors.NEUTRAL_0};
                }

                s {
                  text-decoration: line-through;
                  color: ${colors.NEUTRAL_0};
                }

                /* Non-clickable links styling */
                span[title] {
                  color: ${colors.PRIMARY};
                  text-decoration: underline;
                  cursor: default;
                }

                /* Text alignment classes from Quill */
                .ql-align-left {
                  text-align: left;
                }
                .ql-align-center {
                  text-align: center;
                }
                .ql-align-right {
                  text-align: right;
                }
                .ql-align-justify {
                  text-align: justify;
                }
              `}
              dangerouslySetInnerHTML={{
                __html: renderNotificationContent(item?.data?.body, 200),
              }}
            />
          ) : (
            <Text
              variant='paragraphSmall'
              weight='medium'
              color={colors.NEUTRAL_0}
            >
              {item?.data.body}
            </Text>
          )}
        </div>

        <div
          className='date'
          title={dayjs(item.created_at).format('h:mm a - MMM D, YYYY')}
        >
          <Text
            variant='paragraphXSmall'
            weight='regular'
            color={colors.NEUTRAL_50}
          >
            {getNotificationDate(item?.created_at)}
          </Text>
        </div>
      </div>
      <Popover
        content={popoverContent}
        trigger='click'
        placement='bottomRight'
        open={isOptionsOpen}
        onOpenChange={(open) => {
          console.log('🔄 NotificationItem: Popover visibility changed', {
            id: item.id,
            open,
          })
          setNotificationOpenForDelete(open ? item.id : null)
        }}
        overlayInnerStyle={{
          backgroundColor: colors.HEADER,
          borderRadius: '8px',
          padding: 0,
        }}
        arrow={false}
        zIndex={10000}
        getPopupContainer={(triggerNode) =>
          triggerNode.parentElement || document.body
        }
      >
        <div
          onClick={(e) => e.stopPropagation()}
          css={css`
            display: flex;
          `}
        >
          <MoreVerticalIcon size={24} />
        </div>
      </Popover>
      {/* Status badge - absolutely positioned at bottom right for invitation notifications */}
      {isInvitation && hasInvitationData && (
        <div
          css={css`
            position: absolute;
            bottom: 8px;
            right: 8px;
            z-index: 1;
            ${!isUnread &&
            css`
              bottom: -2px;
            `}
          `}
        >
          <InvitationStatusBadge
            accepted_at={item?.invitation?.accepted_at || null}
          />
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteNotificationModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteModalClose}
        onConfirm={handleDeleteConfirm}
        isLoading={isDeleting}
        error={deleteError}
      />
    </Invite>
  )
}

export default NotificationItem
